import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ArrowRight, BookOpen, Code2 } from 'lucide-react';
import { useLang } from '../i18n/LangContext';
import { SectionEyebrow, SectionTitle, FadeIn, StaggerList, StaggerItem } from '../components/Animated';

const ERP_IMG = 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NTYxODh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzb2Z0d2FyZSUyMGRhc2hib2FyZHxlbnwwfHx8fDE3ODIyMTQwMTN8MA&ixlib=rb-4.1.0&q=85';
const WEB_IMG = 'https://images.pexels.com/photos/27141316/pexels-photo-27141316.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

const ServicesPage = () => {
  const { t, isRTL } = useLang();
  const erpFeatures = t('services.erp_features');
  const webFeatures = t('services.web_features');

  return (
    <div data-testid="services-page" className="relative pt-32 pb-24">
      <div className="absolute inset-0 bg-grid bg-grid-radial pointer-events-none opacity-50" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <FadeIn>
          <SectionEyebrow>{t('services.eyebrow')}</SectionEyebrow>
        </FadeIn>
        <SectionTitle className="mt-3">{t('services.title')}</SectionTitle>
        <FadeIn delay={0.2}>
          <p className="mt-6 text-lg text-zinc-400 max-w-2xl">{t('services.sub')}</p>
        </FadeIn>

        {/* ERP block */}
        <div className="mt-24 grid lg:grid-cols-12 gap-10 items-center">
          <FadeIn className="lg:col-span-6 order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-blue-500/30 bg-blue-500/10 text-blue-400 text-xs uppercase tracking-[0.2em] font-bold heading-font">
              <BookOpen className="w-3.5 h-3.5" /> ERP
            </div>
            <h3 className="mt-5 heading-font text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight">
              {t('services.erp_heading')}
            </h3>
            <p className="mt-4 text-lg text-zinc-400 leading-relaxed">{t('services.erp_intro')}</p>
            <StaggerList className="mt-8 grid sm:grid-cols-2 gap-3">
              {Array.isArray(erpFeatures) && erpFeatures.map((f, i) => (
                <StaggerItem key={i}>
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 w-5 h-5 rounded grid place-items-center bg-blue-600/20 text-blue-400 shrink-0">
                      <Check className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-zinc-300">{f}</span>
                  </div>
                </StaggerItem>
              ))}
            </StaggerList>
          </FadeIn>
          <FadeIn delay={0.2} className="lg:col-span-6 order-1 lg:order-2">
            <motion.div whileHover={{ y: -8 }} transition={{ duration: 0.6 }} className="relative">
              <div className="absolute -inset-4 bg-blue-600/30 blur-3xl rounded-full" />
              <img src={ERP_IMG} alt="ERP" className="relative rounded-2xl border border-white/10 w-full" />
            </motion.div>
          </FadeIn>
        </div>

        {/* Web Dev block */}
        <div className="mt-32 grid lg:grid-cols-12 gap-10 items-center">
          <FadeIn className="lg:col-span-6">
            <motion.div whileHover={{ y: -8 }} transition={{ duration: 0.6 }} className="relative">
              <div className="absolute -inset-4 bg-yellow-400/20 blur-3xl rounded-full" />
              <img src={WEB_IMG} alt="Web" className="relative rounded-2xl border border-white/10 w-full" />
            </motion.div>
          </FadeIn>
          <FadeIn delay={0.2} className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-yellow-400/30 bg-yellow-400/10 text-yellow-400 text-xs uppercase tracking-[0.2em] font-bold heading-font">
              <Code2 className="w-3.5 h-3.5" /> Web
            </div>
            <h3 className="mt-5 heading-font text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight">
              {t('services.web_heading')}
            </h3>
            <p className="mt-4 text-lg text-zinc-400 leading-relaxed">{t('services.web_intro')}</p>
            <StaggerList className="mt-8 grid sm:grid-cols-2 gap-3">
              {Array.isArray(webFeatures) && webFeatures.map((f, i) => (
                <StaggerItem key={i}>
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 w-5 h-5 rounded grid place-items-center bg-yellow-400/20 text-yellow-400 shrink-0">
                      <Check className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-zinc-300">{f}</span>
                  </div>
                </StaggerItem>
              ))}
            </StaggerList>
          </FadeIn>
        </div>

        {/* CTA */}
        <FadeIn>
          <div className="mt-32 relative overflow-hidden rounded-3xl border border-white/10 bg-zinc-950 p-10 md:p-16">
            <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="heading-font text-3xl md:text-5xl font-black text-white">
                  {t('services.cta_title')}
                </h3>
                <p className="mt-4 text-lg text-zinc-400">{t('services.cta_sub')}</p>
              </div>
              <div className="md:text-end">
                <Link
                  to="/contact"
                  data-testid="services-cta"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-blue-600 hover:bg-blue-500 transition font-semibold text-white"
                >
                  {t('nav.cta')}
                  <ArrowRight className={`w-4 h-4 ${isRTL ? 'rtl-flip' : ''}`} />
                </Link>
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </div>
  );
};

export default ServicesPage;
